import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import useScrollReveal from '../hooks/useScrollReveal';
import { createFadeIn } from '../utils/animations';
import ScrollProgressBar from '../components/ScrollProgressBar';
import './VisionPage.css';

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconTruth = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const IconReason = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="18" cy="18" r="3" />
    <circle cx="6" cy="6" r="3" />
    <path d="M6 21V9a9 9 0 0 0 9 9" />
  </svg>
);

const IconHuman = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconMemory = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M9 3H5a2 2 0 0 0-2 2v4" />
    <path d="M15 3h4a2 2 0 0 1 2 2v4" />
    <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
    <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 10v2l1.5 1" />
  </svg>
);

// ─── Network Domain Icons (SVG paths for use inside the diagram) ──────────────

// Returns SVG path data or group for each domain icon (centered at 0,0, ~14px scale)
const domainIconPath = (type) => {
  switch (type) {
    case 'tech':
      return <path d="M-6 -3 L-2 0 L-6 3 M6 -3 L2 0 L6 3 M-1 0 L1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />;
    case 'human':
      return <>
        <circle cx="0" cy="-4.5" r="2.5" stroke="currentColor" strokeWidth="1.6" fill="none" />
        <path d="M-5 4 C-5 0 5 0 5 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      </>;
    case 'knowledge':
      return <>
        <rect x="-5" y="-5.5" width="10" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.6" fill="none" />
        <path d="M-2.5 -2 L2.5 -2 M-2.5 0.5 L2.5 0.5 M-2.5 3 L1 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </>;
    case 'global':
      return <>
        <circle cx="0" cy="0" r="5.5" stroke="currentColor" strokeWidth="1.6" fill="none" />
        <ellipse cx="0" cy="0" rx="2.5" ry="5.5" stroke="currentColor" strokeWidth="1.4" fill="none" />
        <path d="M-5.5 0 L5.5 0" stroke="currentColor" strokeWidth="1.4" />
      </>;
    case 'comms':
      return <>
        <path d="M-5.5 -4 Q-5.5 -6 -3 -6 L3 -6 Q5.5 -6 5.5 -4 L5.5 0 Q5.5 2 3 2 L0 2 L-3 5 L-3 2 Q-5.5 2 -5.5 0 Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </>;
    case 'science':
      return <>
        <circle cx="0" cy="0" r="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <ellipse cx="0" cy="0" rx="5.5" ry="2" stroke="currentColor" strokeWidth="1.4" fill="none" />
        <ellipse cx="0" cy="0" rx="5.5" ry="2" stroke="currentColor" strokeWidth="1.4" fill="none" transform="rotate(60)" />
        <ellipse cx="0" cy="0" rx="5.5" ry="2" stroke="currentColor" strokeWidth="1.4" fill="none" transform="rotate(120)" />
      </>;
    case 'data':
      return <>
        <ellipse cx="0" cy="-3.5" rx="5" ry="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M-5 -3.5 L-5 0 Q-5 2 0 2 Q5 2 5 0 L5 -3.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M-5 0 Q-5 2 0 2 Q5 2 5 0" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="0" />
      </>;
    default:
      return null;
  }
};

// ─── Hero Visual ──────────────────────────────────────────────────────────────

const HeroVisual = () => (
  <div className="vision-hero-visual" aria-hidden="true">
    <svg
      className="vision-hero-svg"
      viewBox="0 0 500 400"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background grid */}
      <defs>
        <pattern id="vgrid" width="24" height="24" patternUnits="userSpaceOnUse">
          <circle cx="0" cy="0" r="1" fill="rgba(26,43,74,0.07)" />
        </pattern>
      </defs>
      <rect width="500" height="400" fill="url(#vgrid)" />

      {/* Stage 1: Terminal window */}
      <g className="vision-node-a" style={{ transformOrigin: '90px 90px', transformBox: 'fill-box' }}>
        <rect x="30" y="60" width="120" height="80" rx="6" fill="var(--bg-primary)" stroke="rgba(26,43,74,0.15)" strokeWidth="1" />
        <rect x="30" y="60" width="120" height="16" rx="6" fill="rgba(26,43,74,0.07)" />
        <rect x="30" y="68" width="120" height="8" fill="rgba(26,43,74,0.07)" />
        <circle cx="42" cy="68" r="3" fill="rgba(26,43,74,0.15)" />
        <circle cx="52" cy="68" r="3" fill="rgba(26,43,74,0.15)" />
        <circle cx="62" cy="68" r="3" fill="rgba(26,43,74,0.15)" />
        <text x="40" y="96" fill="rgba(26,43,74,0.5)" fontSize="7" fontFamily="monospace">$ ask --query</text>
        <text x="40" y="108" fill="rgba(26,43,74,0.35)" fontSize="7" fontFamily="monospace">processing...</text>
        <text x="40" y="120" fill="rgba(26,43,74,0.25)" fontSize="7" fontFamily="monospace">output: result</text>
        <text x="38" y="152" fill="rgba(26,43,74,0.35)" fontSize="7.5" fontFamily="Inter, sans-serif">Commands</text>
      </g>

      {/* Arrow 1→2 */}
      <g opacity="0.3">
        <path d="M158 100 L190 100" stroke="rgba(26,43,74,0.4)" strokeWidth="1.5" strokeDasharray="3 3" />
        <polygon points="190,96 198,100 190,104" fill="rgba(26,43,74,0.4)" />
      </g>

      {/* Stage 2: GUI Window */}
      <g className="vision-node-b" style={{ transformOrigin: '250px 90px', transformBox: 'fill-box' }}>
        <rect x="195" y="55" width="110" height="90" rx="8" fill="var(--bg-primary)" stroke="rgba(26,43,74,0.15)" strokeWidth="1" />
        <rect x="195" y="55" width="110" height="22" rx="8" fill="rgba(26,43,74,0.05)" />
        <rect x="195" y="66" width="110" height="11" fill="rgba(26,43,74,0.05)" />
        <circle cx="205" cy="66" r="3" fill="rgba(220,80,80,0.3)" />
        <circle cx="215" cy="66" r="3" fill="rgba(200,160,40,0.3)" />
        <circle cx="225" cy="66" r="3" fill="rgba(60,180,80,0.3)" />
        <rect x="208" y="82" width="79" height="10" rx="5" fill="rgba(26,43,74,0.07)" />
        <rect x="208" y="98" width="55" height="8" rx="4" fill="rgba(26,43,74,0.07)" />
        <rect x="208" y="112" width="35" height="14" rx="5" fill="var(--accent)" opacity="0.7" />
        <text x="215" y="121.5" fill="rgba(250,250,248,0.9)" fontSize="6.5" fontFamily="Inter, sans-serif">Search</text>
        <text x="218" y="152" fill="rgba(26,43,74,0.35)" fontSize="7.5" fontFamily="Inter, sans-serif">Clicks</text>
      </g>

      {/* Arrow 2→3 */}
      <g opacity="0.3">
        <path d="M310 100 L342 100" stroke="rgba(26,43,74,0.4)" strokeWidth="1.5" strokeDasharray="3 3" />
        <polygon points="342,96 350,100 342,104" fill="rgba(26,43,74,0.4)" />
      </g>

      {/* Stage 3: Conversational — highlighted */}
      <g className="vision-node-c" style={{ transformOrigin: '415px 90px', transformBox: 'fill-box' }}>
        <rect x="355" y="48" width="115" height="100" rx="10" fill="var(--accent)" opacity="0.06" stroke="rgba(26,43,74,0.25)" strokeWidth="1.5" />
        {/* Chat bubbles */}
        <rect x="367" y="60" width="80" height="18" rx="9" fill="rgba(26,43,74,0.08)" />
        <text x="373" y="72.5" fill="rgba(26,43,74,0.55)" fontSize="7" fontFamily="Inter, sans-serif">Can you explain this?</text>
        <rect x="367" y="84" width="95" height="24" rx="9" fill="var(--accent)" opacity="0.85" />
        <text x="373" y="96" fill="rgba(250,250,248,0.9)" fontSize="6.5" fontFamily="Inter, sans-serif">Here's an answer</text>
        <text x="373" y="104" fill="rgba(250,250,248,0.7)" fontSize="6" fontFamily="Inter, sans-serif">built for YOU...</text>
        <rect x="367" y="115" width="65" height="16" rx="8" fill="rgba(26,43,74,0.07)" />
        <text x="373" y="126.5" fill="rgba(26,43,74,0.45)" fontSize="7" fontFamily="Inter, sans-serif">Tell me more</text>
        <text x="398" y="157" fill="var(--accent)" fontSize="7.5" fontFamily="Inter, sans-serif" fontWeight="500">Conversations</text>
      </g>

      {/* Bottom decorative nodes */}
      <g className="vision-node-d">
        <circle cx="100" cy="290" r="32" fill="var(--bg-primary)" stroke="rgba(26,43,74,0.1)" strokeWidth="1" />
        <text x="100" y="285" textAnchor="middle" fill="rgba(26,43,74,0.4)" fontSize="7.5" fontFamily="Inter, sans-serif">Learn</text>
        <text x="100" y="296" textAnchor="middle" fill="rgba(26,43,74,0.4)" fontSize="7.5" fontFamily="Inter, sans-serif">Context</text>
      </g>

      <g className="vision-node-e">
        <circle cx="250" cy="310" r="38" fill="var(--accent)" opacity="0.9" />
        <text x="250" y="305" textAnchor="middle" fill="rgba(250,250,248,0.95)" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="600">Rusiv</text>
        <text x="250" y="318" textAnchor="middle" fill="rgba(250,250,248,0.65)" fontSize="7" fontFamily="Inter, sans-serif">Personal AI</text>
      </g>

      <g className="vision-node-a">
        <circle cx="400" cy="290" r="32" fill="var(--bg-primary)" stroke="rgba(26,43,74,0.1)" strokeWidth="1" />
        <text x="400" y="285" textAnchor="middle" fill="rgba(26,43,74,0.4)" fontSize="7.5" fontFamily="Inter, sans-serif">Adapt</text>
        <text x="400" y="296" textAnchor="middle" fill="rgba(26,43,74,0.4)" fontSize="7.5" fontFamily="Inter, sans-serif">to YOU</text>
      </g>

      {/* Connecting lines from Rusiv center */}
      <line x1="215" y1="308" x2="135" y2="297" stroke="rgba(26,43,74,0.1)" strokeWidth="1" strokeDasharray="3 4" />
      <line x1="285" y1="308" x2="368" y2="296" stroke="rgba(26,43,74,0.1)" strokeWidth="1" strokeDasharray="3 4" />
      <line x1="250" y1="272" x2="250" y2="200" stroke="rgba(26,43,74,0.08)" strokeWidth="1" strokeDasharray="3 5" />

      {/* Stage labels top row */}
      <line x1="90" y1="172" x2="90" y2="250" stroke="rgba(26,43,74,0.06)" strokeWidth="1" strokeDasharray="3 4" />
      <line x1="250" y1="172" x2="250" y2="265" stroke="rgba(26,43,74,0.06)" strokeWidth="1" strokeDasharray="3 4" />
      <line x1="415" y1="172" x2="415" y2="250" stroke="rgba(26,43,74,0.06)" strokeWidth="1" strokeDasharray="3 4" />
    </svg>
  </div>
);

// ─── Intelligence Network ─────────────────────────────────────────────────────

const DOMAIN_DETAILS = {
  tech: {
    label: 'Technology',
    detail: 'The engineering foundation Rusiv runs on: models, workflows, and the infrastructure that keeps them reliable.',
  },
  human: {
    label: 'Human',
    detail: 'The layer that learns how a person communicates, and adapts tone and depth to match.',
  },
  knowledge: {
    label: 'Knowledge',
    detail: 'Reading and reasoning over documents someone shares, not just generating text about them.',
  },
  global: {
    label: 'Global',
    detail: 'Live web search and current information, brought in when an answer needs more than memory.',
  },
  comms: {
    label: 'Language',
    detail: 'Language that shifts in tone and structure depending on what the moment calls for.',
  },
  science: {
    label: 'Science',
    detail: 'Reasoning built for problems that need evidence and comparison, not just a quick guess.',
  },
  data: {
    label: 'Data',
    detail: 'The memory and retrieval systems that let useful context carry forward responsibly.',
  },
};

const IntelligenceNetwork = ({ activeDomain, onDomainSelect }) => {
  const cx = 400;
  const cy = 360; // Center near the bottom
  const r = 240;  // Radius of the arc

  const domains = [
    { key: 'tech',      label: 'Technology',   sublabel: 'AI & Systems',  angle: 180 },
    { key: 'human',     label: 'Human',        sublabel: 'Understanding', angle: 210 },
    { key: 'knowledge', label: 'Knowledge',    sublabel: 'Documents',     angle: 240 },
    { key: 'global',    label: 'Global',       sublabel: 'Information',   angle: 270 },
    { key: 'comms',     label: 'Language',     sublabel: 'Communication', angle: 300 },
    { key: 'science',   label: 'Science',      sublabel: 'Discovery',     angle: 330 },
    { key: 'data',      label: 'Data',         sublabel: 'Intelligence',  angle: 360 },
  ].map((d, i) => ({
    ...d,
    x: cx + r * Math.cos((d.angle * Math.PI) / 180),
    y: cy + r * Math.sin((d.angle * Math.PI) / 180),
    floatClass: `vision-bubble-${i}`,
    lineClass: i % 3 === 0 ? 'vision-net-line' : i % 3 === 1 ? 'vision-net-line-slow' : 'vision-net-line-fast',
  }));

  // Midpoints for data-travel dots
  const midPt = (d) => ({
    mx: cx + (d.x - cx) * 0.5,
    my: cy + (d.y - cy) * 0.5,
  });

  return (
    <svg
      className="vision-network-svg"
      viewBox="0 0 800 430"
      role="img"
      aria-labelledby="vision-network-title vision-network-description"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id="vision-network-title">Allyverto intelligence network</title>
      <desc id="vision-network-description">A diagram showing Allyverto connecting technology, human understanding, knowledge, global information, language, science, and data.</desc>

      {/* Background subtle arc guide */}
      <path
        d={`M ${cx - r - 20} ${cy} A ${r + 20} ${r + 20} 0 0 1 ${cx + r + 20} ${cy}`}
        fill="none"
        stroke="rgba(26,43,74,0.04)"
        strokeWidth="1"
        strokeDasharray="4 8"
      />

      {/* Connection lines from each bubble to center */}
      {domains.map((d, i) => (
        <line
          key={`line-${i}`}
          x1={d.x} y1={d.y}
          x2={cx} y2={cy}
          stroke="rgba(26,43,74,0.18)"
          strokeWidth="1.2"
          className={d.lineClass}
        />
      ))}

      {/* Traveling data dots along lines */}
      {domains.map((d, i) => {
        const { mx, my } = midPt(d);
        return (
          <circle
            key={`dot-${i}`}
            cx={mx}
            cy={my}
            r="2.5"
            fill="var(--accent)"
            opacity="0.5"
            className={`vision-data-dot-${i + 1}`}
          />
        );
      })}

      {/* Central hub — outermost ring */}
      <circle
        cx={cx} cy={cy} r="82"
        fill="none"
        stroke="rgba(26,43,74,0.07)"
        strokeWidth="1"
        strokeDasharray="4 6"
        className="vision-core-ring-outer"
      />

      {/* Central hub — middle ring */}
      <circle
        cx={cx} cy={cy} r="64"
        fill="rgba(26,43,74,0.04)"
        stroke="rgba(26,43,74,0.1)"
        strokeWidth="1"
      />

      {/* Central hub — inner fill */}
      <circle
        cx={cx} cy={cy} r="48"
        fill="var(--accent)"
      />

      {/* Central text */}
      <text
        x={cx} y={cy - 6}
        textAnchor="middle"
        fill="rgba(250,250,248,0.95)"
        fontSize="13"
        fontWeight="600"
        fontFamily="Inter, sans-serif"
        letterSpacing="-0.3"
      >
        Allyverto
      </text>
      <text
        x={cx} y={cy + 11}
        textAnchor="middle"
        fill="rgba(250,250,248,0.55)"
        fontSize="7.5"
        fontFamily="Inter, sans-serif"
        letterSpacing="1.4"
      >
        INTELLIGENCE HUB
      </text>

      {/* Domain bubbles */}
      {domains.map((d, i) => (
        <g
          key={`bubble-${i}`}
          className={d.floatClass}
          style={{ transformOrigin: `${d.x}px ${d.y}px`, transformBox: 'fill-box' }}
        >
          <g
            className={`vision-domain-bubble${activeDomain === d.key ? ' is-active' : ''}`}
            style={{ transformOrigin: `${d.x}px ${d.y}px`, cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            aria-label={`${d.label}: ${DOMAIN_DETAILS[d.key].detail}`}
            aria-pressed={activeDomain === d.key}
            onMouseEnter={() => onDomainSelect(d.key)}
            onMouseLeave={() => onDomainSelect(null)}
            onFocus={() => onDomainSelect(d.key)}
            onBlur={() => onDomainSelect(null)}
            onClick={() => onDomainSelect(activeDomain === d.key ? null : d.key)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onDomainSelect(activeDomain === d.key ? null : d.key);
              }
            }}
          >
          {/* Bubble outer glow */}
          <circle
            cx={d.x} cy={d.y} r="38"
            fill="rgba(26,43,74,0.03)"
            stroke="rgba(26,43,74,0.06)"
            strokeWidth="1"
          />
          {/* Bubble main */}
          <circle
            cx={d.x} cy={d.y} r="30"
            fill="rgba(250,250,248,0.85)"
            stroke="rgba(26,43,74,0.14)"
            strokeWidth="1.5"
            style={{
              filter: 'drop-shadow(0 4px 12px rgba(26,43,74,0.08))',
            }}
          />
          {/* Domain icon */}
          <g
            transform={`translate(${d.x}, ${d.y - 7})`}
            fill="none"
            stroke="var(--accent)"
          >
            {domainIconPath(d.key)}
          </g>
          {/* Label */}
          <text
            x={d.x} y={d.y + 16}
            textAnchor="middle"
            fill="var(--accent)"
            fontSize="7"
            fontWeight="600"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.5"
          >
            {d.label.toUpperCase()}
          </text>
          {/* Sub-label below bubble */}
          <text
            x={d.x}
            y={d.y + (d.y < cy ? -44 : 50)}
            textAnchor="middle"
            fill="rgba(26,43,74,0.45)"
            fontSize="8"
            fontFamily="Inter, sans-serif"
          >
            {d.sublabel}
          </text>
          </g>
        </g>
      ))}

      {/* Ambient floating dots */}
      {[
        [60, 40], [740, 50], [20, 260], [780, 240],
        [140, 400], [660, 395], [400, 18],
      ].map(([x, y], i) => (
        <circle key={`amb-${i}`} cx={x} cy={y} r="2" fill="rgba(26,43,74,0.12)" />
      ))}
    </svg>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const VisionPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [activeDomain, setActiveDomain] = useState(null);

  useScrollReveal();

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 60);
    window.scrollTo(0, 0);
    return () => clearTimeout(timer);
  }, []);

  const fi = createFadeIn(mounted);

  // ── Data ──────────────────────────────────────────

  const shifts = [
    {
      from: 'Query',
      to: 'Relationship',
      desc: 'A single exchange becomes something the system can build on, not a form to fill out each time.',
    },
    {
      from: 'Average',
      to: 'Individual',
      desc: 'Software stops optimising for the average user and starts responding to the one in front of it.',
    },
    {
      from: 'Fixed',
      to: 'Evolving',
      desc: 'What a system knows about someone stays current, instead of freezing at the moment they signed up.',
    },
    {
      from: 'Output',
      to: 'Judgement',
      desc: 'The goal shifts from generating an answer to helping someone reach a better decision of their own.',
    },
    {
      from: 'Hidden',
      to: 'Visible',
      desc: 'People can see and question what a system has learned about them, instead of trusting a black box.',
    },
    {
      from: 'Tool',
      to: 'Partner',
      desc: 'The relationship feels less like operating software and more like working alongside something that knows the context.',
    },
  ];

  const visionBullets = [
    { text: 'Start with education, where the need for personalisation is easiest to see and prove.' },
    { text: 'Extend into everyday decisions, from small choices to the ones that shape a year.' },
    { text: 'Support real work: research, planning, and the follow-through that turns ideas into outcomes.' },
    { text: 'Stay useful in quieter, personal moments that do not fit neatly into a use case.' },
    { text: 'Grow only as far as trust and consent allow, never faster than people are comfortable with.' },
  ];

  const futureStatements = [
    {
      text: 'Intelligence becomes personal, continuous, and grows as YOU do.',
      desc: 'A system that carries understanding forward so YOU never have to reintroduce yourself.',
    },
    {
      text: 'Software understands not only the request, but the person and the intent behind it.',
      desc: 'Technology adapts to people instead of forcing people to adapt to technology.',
    },
    {
      text: 'AI helps people move from thought to outcome: understand, decide, create, and act.',
      desc: 'One coherent partner across research, planning, learning, building, and everyday execution.',
    },
    {
      text: 'Human judgement remains central, even as intelligence becomes more capable.',
      desc: 'People stay informed, in control, and responsible for the choices that shape their lives.',
    },
  ];

  const philosophyCards = [
    {
      Icon: IconTruth,
      title: 'Confidence Has Limits',
      desc: 'A system should say what it does not know, rather than presenting a guess as settled fact.',
    },
    {
      Icon: IconReason,
      title: 'Reasoning Before Responding',
      desc: 'Working through a problem carefully matters more than sounding confident quickly.',
    },
    {
      Icon: IconHuman,
      title: 'Augmentation, Not Autonomy',
      desc: 'Progress is measured by whether people make better decisions with the system, not by how independently the system can act.',
    },
    {
      Icon: IconMemory,
      title: 'Memory Has to Earn Its Place',
      desc: 'Not everything worth remembering is worth keeping. What persists should be useful, not just available.',
    },
  ];

  // ── Render ────────────────────────────────────────

  return (
    <main className="vision-page">
      <ScrollProgressBar />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="vision-hero">
        <div className="vision-hero-dots" />
        <div className="vision-hero-glow" />
        <div className="container">
          <div className="vision-hero-inner">

            {/* Left: Copy */}
            <div className="vision-hero-copy">
              <div className="vision-hero-badge" style={fi(0.1)}>
                <span className="vision-hero-badge-dot" />
                Allyverto · Personal AI, built around you
              </div>

              <h1
                style={{
                  ...fi(0.2),
                  fontFamily: 'var(--font-serif)',
                  fontStyle: 'italic',
                  fontSize: 'clamp(2.8rem, 6vw, 5rem)',
                  lineHeight: '1.08',
                  marginBottom: '1.5rem',
                }}
              >
                Building the{' '}
                <span style={{ fontStyle: 'normal', color: 'var(--accent)' }}>intelligence</span>
                {' '}that grows with you.
              </h1>

              <p
                style={{
                  ...fi(0.35),
                  fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                  lineHeight: '1.8',
                  color: 'var(--text-secondary)',
                  marginBottom: '2.25rem',
                  maxWidth: '460px',
                }}
              >
                What Rusiv does today is only a starting point. The larger
                goal is AI that begins with the person, not the prompt, and
                keeps learning who that person is becoming.
              </p>

              <div style={fi(0.5)}>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '0.65rem 1.25rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                    fontFamily: 'var(--font-serif)',
                  }}
                >
                  Intelligence should feel less like operating software and more like being understood.
                </div>
              </div>
            </div>

            {/* Right: Visual */}
            <div style={fi(0.4)}>
              <HeroVisual />
            </div>

          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ EVOLUTION ━━━━━━━━━━━━━━━━━━━ */}
      <section className="vision-evolution-section" style={{ padding: 'clamp(5rem, 10vw, 8rem) 0', background: 'var(--bg-secondary)' }}>
        <div className="container">

          <div data-scroll style={{ textAlign: 'center', marginBottom: 'clamp(3rem, 5vw, 4.5rem)' }}>
            <div className="vision-label">The Evolution</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                marginBottom: '1rem',
              }}
            >
              Software is{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>changing.</span>
            </h2>
            <p
              style={{
                fontSize: '1.05rem',
                lineHeight: '1.75',
                color: 'var(--text-secondary)',
                maxWidth: '520px',
                margin: '0 auto',
              }}
            >
              The interface between humans and computers has always evolved.
              The next step is the most significant: software that understands
              the person, not just the command.
            </p>
          </div>

          <div className="vision-evolution-grid" data-scroll>
            {[
              {
                num: '01',
                era: 'The Command Era',
                headline: 'You speak machine.',
                desc: 'Precise syntax. Exact commands. Computers demanded humans learn their language.',
                active: false,
              },
              {
                num: '02',
                era: 'The Graphical Era',
                headline: 'You click and tap.',
                desc: 'Visual interfaces made software accessible. But you still navigate menus to find what you need.',
                active: false,
              },
              {
                num: '03',
                era: 'The Personal Era',
                headline: 'Software understands YOU.',
                desc: 'Interfaces become personal. Systems understand who is asking, not just what is asked.',
                active: true,
              },
            ].map((e, i) => (
              <div
                key={i}
                className={`vision-evolution-card${e.active ? ' active' : ''}`}
                data-scroll
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="vision-evolution-num">{e.num}</div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: e.active ? 'var(--accent)' : 'var(--text-muted)',
                    marginBottom: '0.6rem',
                    opacity: e.active ? 0.8 : 0.6,
                  }}
                >
                  {e.era}
                </div>
                <h3
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    color: e.active ? 'var(--text-primary)' : 'var(--text-secondary)',
                    marginBottom: '0.65rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {e.headline}
                </h3>
                <p
                  style={{
                    fontSize: '0.88rem',
                    lineHeight: '1.65',
                    color: 'var(--text-muted)',
                    margin: 0,
                  }}
                >
                  {e.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ AI ASSISTANTS ━━━━━━━━━━━━━━━━━━ */}
      <section className="vision-section vision-role-section">
        <div className="container">

          <div className="vision-role-layout">
            <div data-scroll>
              <div className="vision-label">The Role</div>
              <h2
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                  lineHeight: '1.1',
                  marginBottom: '1.25rem',
                }}
              >
                What personal intelligence{' '}
                <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>can become.</span>
              </h2>
              <p
                style={{
                  fontSize: '1.05rem',
                  lineHeight: '1.8',
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem',
                }}
              >
                Personal AI does not just add more knowledge. It changes the
                basic shape of the relationship between a person and the
                system helping them. Six shifts describe what that looks like.
              </p>
              <div
                style={{
                  padding: '1rem 1.5rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  borderLeft: '3px solid var(--accent)',
                  background: 'rgba(26,43,74,0.025)',
                }}
              >
                <p
                  style={{
                    fontSize: '0.9rem',
                    lineHeight: '1.7',
                    color: 'var(--text-muted)',
                    margin: 0,
                    fontStyle: 'italic',
                    fontFamily: 'var(--font-serif)',
                  }}
                >
                  “Our goal is intelligence people trust with real choices,
                  not just quick answers.”
                </p>
              </div>
            </div>

            <div className="vision-shift-list" data-scroll style={{ transitionDelay: '0.15s' }}>
              {shifts.map(({ from, to, desc }, i) => (
                <article key={to} className="vision-shift-row" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <span className="vision-shift-index" aria-hidden="true">0{i + 1}</span>
                  <span className="vision-shift-terms">
                    <span className="vision-shift-from">{from}</span>
                    <span className="vision-shift-arrow" aria-hidden="true">→</span>
                    <span className="vision-shift-to">{to}</span>
                  </span>
                  <p>{desc}</p>
                </article>
              ))}
            </div>

          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ LONG-TERM VISION ━━━━━━━━━━━━━━━━ */}
      <section className="vision-section vision-section--secondary">
        <div className="container">
          <div className="vision-ltv-grid">

            <div data-scroll>
              <div className="vision-label">Long-Term Vision</div>
              <h2
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                  lineHeight: '1.1',
                  marginBottom: '1.5rem',
                }}
              >
                Building intelligence worthy of{' '}
                <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>trust.</span>
              </h2>
              <p
                style={{
                  fontSize: '1.05rem',
                  lineHeight: '1.8',
                  color: 'var(--text-secondary)',
                }}
              >
                The long-term goal reaches beyond any single feature or release.
                Here is how we think about where this goes.
              </p>
            </div>

            <div data-scroll style={{ transitionDelay: '0.15s' }}>
              {visionBullets.map((b, i) => (
                <div key={b.text} className="vision-ltv-item">
                  <div className="vision-ltv-index" aria-hidden="true">0{i + 1}</div>
                  <div className="vision-ltv-dash" />
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: 'var(--text-secondary)',
                      margin: 0,
                    }}
                  >
                    {b.text}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ FUTURE WE IMAGINE ━━━━━━━━━━━━━━━━ */}
      <section className="vision-imagine-section">
        <div className="container" style={{ position: 'relative' }}>

          <div data-scroll style={{ marginBottom: 'clamp(2.5rem, 5vw, 4rem)' }}>
            <div className="vision-label">The Future We Imagine</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                lineHeight: '1.1',
              }}
            >
              A world shaped by{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>who is asking.</span>
            </h2>
          </div>

          <div>
            {futureStatements.map((s, i) => (
              <div
                key={i}
                className="vision-statement-row"
                data-scroll
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <div className="vision-statement-num">0{i + 1}</div>
                <div className="vision-statement-text">{s.text}</div>
                <div className="vision-statement-desc">{s.desc}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ TECH PHILOSOPHY ━━━━━━━━━━━━━━━━━━━ */}
      <section className="vision-section vision-section--secondary">
        <div className="container">

          <div data-scroll style={{ textAlign: 'center', marginBottom: 'clamp(3rem, 5vw, 4.5rem)' }}>
            <div className="vision-label">Technology Philosophy</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
              }}
            >
              How we{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>think about AI.</span>
            </h2>
          </div>

          <div className="vision-philosophy-grid">
            {philosophyCards.map(({ Icon, title, desc }, i) => (
              <article
                key={title}
                className="vision-philosophy-item"
                data-scroll
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="vision-philosophy-icon">
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ INTELLIGENCE NETWORK ━━━━━━━━━━━━━━━ */}
      <section className="vision-network-section">
        <div className="container">

          <div data-scroll style={{ textAlign: 'center', marginBottom: 'clamp(2.5rem, 5vw, 4rem)' }}>
            <div className="vision-label">The Allyverto Intelligence Layer</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                marginBottom: '1rem',
              }}
            >
              One intelligence, connected across{' '}
              <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>every domain.</span>
            </h2>
            <p
              style={{
                fontSize: '1.05rem',
                lineHeight: '1.75',
                color: 'var(--text-secondary)',
                maxWidth: '520px',
                margin: '0 auto',
              }}
            >
              Allyverto develops the foundation. Rusiv brings it into daily life by connecting
              personal context with knowledge, language, and human understanding.
            </p>
          </div>

          <div className="vision-network-container" data-scroll>
            <IntelligenceNetwork
              activeDomain={activeDomain}
              onDomainSelect={setActiveDomain}
            />
            <p className="vision-network-caption" aria-live="polite">
              {activeDomain ? (
                <>
                  <strong>{DOMAIN_DETAILS[activeDomain].label}.</strong>{' '}
                  {DOMAIN_DETAILS[activeDomain].detail}
                </>
              ) : (
                <>
                  This diagram is Allyverto's actual scope: not one product, but a
                  foundation broad enough for Rusiv, and whatever we build after it.
                  <span className="vision-network-hint"> Hover or tap a domain to learn more.</span>
                </>
              )}
            </p>
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="vision-cta">
        <div
          className="container-narrow"
          style={{ textAlign: 'center', position: 'relative' }}
        >
          <div data-scroll>
            <div className="vision-label">Join the Future</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                lineHeight: '1.1',
                marginBottom: '1.25rem',
                color: 'var(--text-primary)',
              }}
            >
              Help build what comes next.
            </h2>
            <p
              style={{
                fontSize: '1.08rem',
                lineHeight: '1.75',
                color: 'var(--text-secondary)',
                maxWidth: '460px',
                margin: '0 auto 2.5rem',
              }}
            >
              Whether you're here to try Rusiv, explore a partnership, or just
              see where this is headed, we'd like to hear from you.
            </p>
            <div className="vision-cta-buttons">
              <button
                type="button"
                className="btn alluse-button alluse-exe-link-button"
                aria-label="Open Rusiv in a new tab"
                onClick={() =>
                  window.open("https://rusiv.com/", "_blank", "noopener,noreferrer")
                }
              >
                Try Rusiv
              </button>
              <button
                type="button"
                className="btn alluse-button"
                onClick={() => navigate("/about")}
              >
                  Learn about Allyverto
              </button>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default VisionPage;
